import type { ConsultantRosterRecord } from "./types";

const PRACTICE_MAP: Record<string, string> = {
  te: "te",
  vc: "vc",
  pi: "vc",
  latam: "latam",
  jvp: "jvp",
  "t&r": "tr",
  tr: "tr",
  "pi healthcare": "pi_healthcare",
  pi_healthcare: "pi_healthcare",
  tas: "tas",
  ocfo: "ocfo",
  digital: "digital",
  dp: "digital",
  "digital products": "digital",
  eu: "eu",
};

function parseRows(csv: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let quoted = false;

  for (let index = 0; index < csv.length; index += 1) {
    const character = csv[index]!;
    if (quoted) {
      if (character === '"' && csv[index + 1] === '"') {
        field += '"';
        index += 1;
      } else if (character === '"') {
        quoted = false;
      } else {
        field += character;
      }
      continue;
    }
    if (character === '"') {
      quoted = true;
    } else if (character === ",") {
      row.push(field);
      field = "";
    } else if (character === "\n") {
      row.push(field.replace(/\r$/, ""));
      rows.push(row);
      row = [];
      field = "";
    } else {
      field += character;
    }
  }
  if (quoted) throw new Error("The CSV ends inside a quoted value.");
  if (field.length > 0 || row.length > 0) {
    row.push(field.replace(/\r$/, ""));
    rows.push(row);
  }
  return rows;
}

function compact(value: string): string {
  return value.trim().replace(/\s+/g, " ");
}

export function parseConsultantRosterCsv(csv: string): ConsultantRosterRecord[] {
  const rows = parseRows(csv.replace(/^\uFEFF/, "")).filter((row) =>
    row.some((value) => value.trim().length > 0)
  );
  if (rows.length < 2) throw new Error("The CSV does not contain any consultant rows.");

  const headers = rows[0]!.map((header) => compact(header).toLowerCase());
  const indexFor = (...candidates: string[]) =>
    headers.findIndex((header) => candidates.includes(header));
  const nameIndex = indexFor("name");
  const emailIndex = indexFor("email address", "email");
  const practiceIndex = indexFor("practice");
  const titleIndex = indexFor("role", "title");
  if (nameIndex < 0 || emailIndex < 0 || practiceIndex < 0 || titleIndex < 0) {
    throw new Error("CSV headers must include Name, Email address, Practice, and Role.");
  }

  return rows.slice(1).map((row, index) => {
    const name = compact(row[nameIndex] ?? "");
    const email = compact(row[emailIndex] ?? "").toLowerCase();
    const practiceLabel = compact(row[practiceIndex] ?? "").toLowerCase();
    const practice = PRACTICE_MAP[practiceLabel];
    const title = compact(row[titleIndex] ?? "") || null;
    if (!name || !email || !practice) {
      throw new Error(`Row ${index + 2} is missing a name, email, or recognized practice.`);
    }
    return { name, email, practice, title };
  });
}
